/**
 * User reports as returned by the admin report queue (SSA-1578), filed from the
 * consumer app's report sheet (SSA-1577).
 *
 * `details` and `context` are personal data about two users: render them, never
 * log them or store them outside component state.
 */

export type ReportStatus = 'open' | 'dismissed' | 'actioned';

export type ReportCategory =
  | 'harassment'
  | 'inappropriate_profile'
  | 'safety_threat'
  | 'spam_or_scam'
  | 'underage'
  | 'other';

export type ReportAction = 'dismiss' | 'restrict' | 'lift_restriction';

export interface ReportVenue {
  businessId: string;
  name: string | null;
}

/** A side of the report as it was at the moment it was filed. */
export interface ReportSideSnapshot {
  venue: ReportVenue | null;
  /** Always null until the going-out vibe ships (SSA-1582). */
  vibe: string | null;
}

export interface ReportedSnapshot extends ReportSideSnapshot {
  displayName: string | null;
  /** The account's last approved picture (a public CDN URL), or null. */
  profilePicture: string | null;
  profilePictureStatus: string | null;
}

/** Built on the server when the report was filed; never sent by the client. */
export interface ReportContext {
  capturedAt?: string;
  reporter?: ReportSideSnapshot;
  reported?: ReportedSnapshot;
  relationship?: 'friends' | 'request_pending' | 'none';
  anyFriendRequest?: boolean;
}

export interface UserReport {
  id: string;
  createdAt: string;
  category: ReportCategory;
  details: string | null;
  context: ReportContext;
  status: ReportStatus;
  resolutionNote: string | null;
  resolvedByEmail: string | null;
  resolvedAt: string | null;
  /** Null once the reporter's account has been erased. */
  reporter: { id: string; displayName: string | null } | null;
  reported: {
    id: string;
    displayName: string | null;
    /** Currently removed from the in-venue stranger list. */
    restricted: boolean;
    /** Open reports about this user right now, this one included if open. */
    openReportCount: number;
  };
}

export const CATEGORY_LABELS: Record<ReportCategory, string> = {
  harassment: 'Harassment',
  inappropriate_profile: 'Inappropriate profile',
  safety_threat: 'Safety concern or threat',
  spam_or_scam: 'Spam or scam',
  underage: 'Under 18',
  other: 'Something else',
};

/** Same-day categories in the runbook (SSA-1589); the owner email marks them [URGENT]. */
export const URGENT_CATEGORIES: ReadonlySet<ReportCategory> = new Set(['safety_threat', 'underage']);

export const NOTE_MIN = 5;
export const NOTE_MAX = 500;
